// src/lib/queryClient.js
// Shared React Query client + a central query-key factory.
//
// Why a key factory: caching is only safe if writes can reliably invalidate the
// exact reads they affect. Centralizing the keys here means an upload/save can
// call `queryKeys.reportPdf(...)` etc. and be guaranteed to match the read.
//
// Freshness philosophy: keep data reasonably fresh without hammering the API.
// Writes invalidate their own reads (see useMutation hooks), so most updates are
// picked up instantly without relying on background refetches. We only refetch
// on focus/reconnect when the cached data is actually STALE (not on every single
// tab switch) — that alone removes the burst of calls you saw when returning to
// the browser.

import { QueryClient } from "@tanstack/react-query";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Data is considered fresh for 2 min. While fresh, re-mounting a component
      // or refocusing the tab uses the cache instead of hitting the API.
      staleTime: 2 * 60 * 1000,
      // Keep unused data around for 10 min so back/forward navigation is instant.
      gcTime: 10 * 60 * 1000,
      // "stale" (not true): only refetch on focus/reconnect if the data has
      // actually gone stale — avoids a refetch storm on every tab switch.
      refetchOnWindowFocus: "stale",
      refetchOnReconnect: "stale",
      retry: 1,
    },
  },
});

// ─── Central query-key factory ───
// All keys are arrays namespaced by domain so invalidation can be broad
// (invalidate everything for a function) or narrow (one report/week/key).
export const queryKeys = {
  functionList: () => ["functionList"],

  permissions: (login, functionId, year) =>
    ["permissions", functionId, String(year), login],

  // Merged config for a single week (details + sidebar + poc + roles)
  config: (functionId, week, year) =>
    ["config", functionId, String(year), String(week)],

  // All-weeks meeting roster (admin Meeting Roles tab)
  meetingRoles: (functionId, year) =>
    ["meetingRoles", functionId, String(year)],

  accessList: (functionId, year) =>
    ["accessList", functionId, String(year)],

  // Which report keys have a PDF uploaded (sidebar check/x icons)
  reportStatus: (functionId, year, week) =>
    ["reportStatus", functionId, String(year), String(week)],

  // Per-report reads
  reportPdf: (functionId, key, week, year) =>
    ["reportPdf", functionId, String(year), String(week), key],
  reportCallout: (functionId, key, week, year) =>
    ["reportCallout", functionId, String(year), String(week), key],
  insights: (functionId, key, week, year) =>
    ["insights", functionId, String(year), String(week), key],

  // Data screens
  actionTracker: (functionId, year) =>
    ["actionTracker", functionId, String(year)],
  businessUpdates: (functionId, year, week, key) =>
    ["businessUpdates", functionId, String(year), String(week), key],
  allBusinessUpdates: (functionId, year, week) =>
    ["allBusinessUpdates", functionId, String(year), String(week)],
  callouts: (functionId, year, week, key) =>
    ["callouts", functionId, String(year), String(week), key],
  questions: (functionId, key, week, year) =>
    ["questions", functionId, String(year), String(week), key],

  aiPrompts: (functionId, year) => ["aiPrompts", functionId, String(year)],
};

// ─── Invalidation helpers ───
// Grouped so a write can invalidate everything its change could have affected,
// without the caller needing to remember every related key.

/**
 * After a report PDF upload for (functionId, year, week, key):
 * the PDF, its AI insights, and the sidebar report-status all change.
 * (Callout is not touched by an upload, so we leave it.)
 */
export function invalidateAfterUpload(client, functionId, year, week, key) {
  client.invalidateQueries({ queryKey: queryKeys.reportPdf(functionId, key, week, year) });
  client.invalidateQueries({ queryKey: queryKeys.insights(functionId, key, week, year) });
  client.invalidateQueries({ queryKey: queryKeys.reportStatus(functionId, year, week) });
}

export function invalidateBusinessUpdates(client, functionId, year, week, key) {
  client.invalidateQueries({ queryKey: queryKeys.businessUpdates(functionId, year, week, key) });
  client.invalidateQueries({ queryKey: queryKeys.allBusinessUpdates(functionId, year, week) });
}

export function invalidateCallouts(client, functionId, year, week, key) {
  client.invalidateQueries({ queryKey: queryKeys.callouts(functionId, year, week, key) });
  client.invalidateQueries({ queryKey: queryKeys.reportCallout(functionId, key, week, year) });
}

export function invalidateActionTracker(client, functionId, year) {
  client.invalidateQueries({ queryKey: queryKeys.actionTracker(functionId, year) });
}

/**
 * Admin config save can affect any week (single/range/all), so invalidate the
 * whole "config" domain plus the meeting roster.
 */
export function invalidateConfig(client, functionId) {
  client.invalidateQueries({ queryKey: ["config", functionId] });
  client.invalidateQueries({ queryKey: ["meetingRoles", functionId] });
}
